import clsx from 'clsx'
import {useFormik} from 'formik'
import {find, forEach, includes, isEmpty, omit} from 'lodash'
import React, {useCallback, useMemo, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import UseRoutesPermission from '../../../../../../_gori/hooks/UseRoutesPermission'
import UseYupValidate from '../../../../../../_gori/hooks/UseYupValidate'
import {
  Button,
  InputEmailFormik,
  InputTextFormik,
  SelectFormik,
  ValidationErrorModal,
} from '../../../../../../_gori/partials/widgets'
import {AutocompleteAddress} from '../../../../common'
import {ConfigGeneral, PROFILE_EDIT_FIELD} from '../../../core/_const'
import useCancelToken from '../../../../../../_gori/hooks/UseCancelToken'
import {toast} from 'react-toastify'
import SettingsService from '../../../core/_requests'
import UserService from '../../../../users/core/_requests'
import {useAuth} from '../../../../auth'
import {VerificationPhoneModal} from '../../../../auth/Modal/VerificationPhoneModal'
import {InputInternationalPhoneFormik} from '../../../../../../_gori/partials/widgets/form/InputInternationalPhoneFormik'
import {StorageHelpers} from '../../../../../../_gori/helpers'
import AuthService from '../../../../auth/core/_requests'
import {OrdersConfig} from '../../../../orders/core/_const'
import OrderService from '../../../../orders/core/_requests'

type Props = {
  show: boolean
  handleClose: () => void
  company: any
}

const CompanyEditModal: React.FC<Props> = ({show, handleClose, company}) => {
  const intl = useIntl()
  const {routes} = UseRoutesPermission()
  const {handleSaveCurrentUser, currentUser} = useAuth()
  const {stringYup, infoYup, regexYup} = UseYupValidate()
  const {isCancel, newCancelToken} = useCancelToken()
  const [validationErrors, setValidationErrors] = useState<any>()
  const [verifiedPhone, setVerifiedPhone] = useState<boolean>(currentUser?.phone_verified ?? false)
  const [, setVerifiedAddress] = useState<boolean>(false)
  const [errorMessageVerifiedPhone, setErrorMessageVerifiedPhone] = useState(false)
  const [showModal, setShowModal] = useState<{
    verify_phone: boolean
  }>({
    verify_phone: false,
  })
  const [modalData, setModalData] = useState<{
    verify_phone: any
  }>({
    verify_phone: [],
  })

  const validateSchema = useMemo(() => {
    const required = routes.ADMINS.hasPermission
    return {
      phone: infoYup.phone('PHONE', required),
      monthly_shipment_volume: stringYup(255, 'SHIPMENT_VOLUME', required),
      website_url: regexYup.websiteUrl('COMPANY_WEBSITE', false),
      sales_rep: regexYup.inputText('SALE_REP', false, 50),
      country: stringYup(255, 'COUNTRY', required),
      street1: stringYup(255, 'ADDRESS', required),
      street2: stringYup(255, 'ADDRESS_2', false),
      city: stringYup(255, 'CITY', required),
      zip: Yup.string().when(`country`, {
        is: (val) => val === 'US',
        then: regexYup.zipCode('POSTAL_CODE', required),
        otherwise: stringYup(255, 'POSTAL_CODE', required),
      }),
      state: Yup.string().when(`country`, {
        is: (val) => val === 'US',
        then: regexYup.state('STATE', required),
        otherwise: stringYup(255, 'STATE', required),
      }),
      report_name: Yup.string().when('report_emails', {
        is: (emails: any[]) => emails?.length > 0,
        then: () => regexYup.inputText('NAME', true, 100),
        otherwise: () => regexYup.inputText('NAME', false, 100),
      }),
      report_emails: Yup.lazy(() => {
        return required
          ? Yup.array()
              .max(
                ConfigGeneral.MAX_EMAIL_NUMBER,
                intl.formatMessage(
                  {id: 'ONLY_UP_TO_INPUT_EMAIL_ADDRESSES_ARE_ALLOWED'},
                  {input: ConfigGeneral.MAX_EMAIL_NUMBER}
                )
              )
              .when('report_name', {
                is: (report_name) => report_name?.trim(),
                then: Yup.array().min(1, intl.formatMessage({id: 'REQUIRED'})),
              })
          : Yup.array()
      }),
      report_email: Yup.lazy(() => {
        return required
          ? infoYup.email('EMAIL', false).test({
              name: 'unique',
              test: function (value) {
                const emails = this.parent.report_emails || []
                return !emails?.map((item) => item?.toLowerCase()).includes(value?.toLowerCase())
              },
              message: intl.formatMessage(
                {id: 'INPUT_ALREADY_EXISTS'},
                {input: intl.formatMessage({id: 'EMAIL'})}
              ),
            })
          : infoYup.email('EMAIL', false)
      }),
      accounting_name: Yup.string().when('accounting_emails', {
        is: (emails: any[]) => emails?.length > 0,
        then: () => regexYup.inputText('NAME', true, 100),
        otherwise: () => regexYup.inputText('NAME', false, 100),
      }),
      accounting_emails: Yup.lazy(() => {
        return required
          ? Yup.array()
              .max(
                ConfigGeneral.MAX_EMAIL_NUMBER,
                intl.formatMessage(
                  {id: 'ONLY_UP_TO_INPUT_EMAIL_ADDRESSES_ARE_ALLOWED'},
                  {input: ConfigGeneral.MAX_EMAIL_NUMBER}
                )
              )
              .when('accounting_name', {
                is: (accounting_name) => accounting_name?.trim(),
                then: Yup.array().min(1, intl.formatMessage({id: 'REQUIRED'})),
              })
          : Yup.array()
      }),
      accounting_email: Yup.lazy(() => {
        return required
          ? infoYup.email('EMAIL', false).test({
              name: 'unique',
              test: function (value) {
                const emails = this.parent.accounting_emails || []
                return !emails?.map((item) => item?.toLowerCase()).includes(value?.toLowerCase())
              },
              message: intl.formatMessage(
                {id: 'INPUT_ALREADY_EXISTS'},
                {input: intl.formatMessage({id: 'EMAIL'})}
              ),
            })
          : infoYup.email('EMAIL', false)
      }),
    }
  }, [routes, stringYup, infoYup, regexYup, intl])

  const initialValues = useMemo(() => {
    let init: any = {}
    forEach(
      [
        ...PROFILE_EDIT_FIELD.ABOUT_YOUR,
        ...PROFILE_EDIT_FIELD.BUSINESS_ADDRESS,
        ...PROFILE_EDIT_FIELD.PRIMARY_CONTACT,
      ],
      (field: any) => {
        if (!isEmpty(company)) {
          switch (field.name) {
            case 'company_name':
              init[field?.name] = company?.name ?? ''
              break
            case 'report_email':
              init[field?.name] = ''
              init[field?.nameParent] = company?.report?.emails ?? []
              break
            case 'report_name':
              init[field?.name] = company?.report?.name ?? ''
              break
            case 'accounting_email':
              init[field?.name] = ''
              init[field?.nameParent] = company?.accounting?.emails ?? []
              break
            case 'accounting_name':
              init[field?.name] = company?.accounting?.name ?? ''
              break
            default:
              init[field.name] = company?.[`${field.name}`]
              break
          }
        }
      }
    )

    return init
  }, [company])

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: Yup.object().shape(validateSchema),
    onSubmit: async (values, {setSubmitting}) => {
      try {
        if (!verifiedPhone) {
          setErrorMessageVerifiedPhone(true)
          return
        }
        setSubmitting(true)

        const isValidAddress = await verifyAddress(values)
        if (!isValidAddress) {
          return
        }

        const payload = {
          ...omit(values, [
            'report_name',
            'report_email',
            'report_emails',
            'accounting_name',
            'accounting_email',
            'accounting_emails',
          ]),
          report: {
            name: values.report_name,
            emails: values.report_emails,
          },
          accounting: {
            name: values.accounting_name,
            emails: values.accounting_emails,
          },
          type: 'company',
        }
        const config = {cancelToken: newCancelToken()}
        const res = await SettingsService.accountEdit(payload, config)
        if (res) {
          const user = await UserService.getUser(config)
          handleSaveCurrentUser(user)
        }

        toast.success(intl.formatMessage({id: 'UPDATED_SUCCESSFULLY'}))
      } catch (error: any) {
        if (isCancel(error)) return
        setValidationErrors(error?.response)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const handleCloseModal = () => {
    handleClose()
    formik.resetForm()
  }

  const selectedPlace = (selected) => {
    let city = find(selected.address_components, (item) => {
      return includes(item?.types, 'locality')
    })
    formik.setFieldValue(formik.getFieldProps(`city`).name, city?.long_name ?? '')
    let state = find(selected.address_components, (item) => {
      return includes(item?.types, 'administrative_area_level_1')
    })
    formik.setFieldValue(formik.getFieldProps(`state`).name, state?.short_name ?? '')
    let country = find(selected.address_components, (item) => {
      return includes(item?.types, 'country')
    })
    formik.setFieldValue(formik.getFieldProps(`country`).name, country?.short_name ?? '')
    let zipCode = find(selected.address_components, (item) => {
      return includes(item?.types, 'postal_code')
    })
    let suffix = find(selected.address_components, (item) => {
      return includes(item?.types, 'postal_code_suffix')
    })
    if (zipCode?.long_name) {
      zipCode = zipCode?.long_name + (suffix?.long_name ? `-${suffix?.long_name}` : '')
    } else {
      zipCode = ''
    }
    formik.setFieldValue('zip', zipCode)
  }

  const checkChangePhone = (value) => {
    if (initialValues.phone === value) {
      setVerifiedPhone(true)
      setErrorMessageVerifiedPhone(false)
      return
    }
    const phoneVerified = StorageHelpers.getItemLocalStorage('PHONE_VERIFIED')
    if (!isEmpty(phoneVerified)) {
      if (value === phoneVerified) {
        setVerifiedPhone(true)
        setErrorMessageVerifiedPhone(false)
        return
      }
    }

    setVerifiedPhone(false)
  }

  const sendSmsOTP = useCallback(async () => {
    const payload = {
      phone: formik.values.phone,
    }
    const config = {cancelToken: newCancelToken()}
    await AuthService.sendOTP(payload, config)
      .then(async (response) => {
        if (response.whitelisted) {
          setVerifiedPhone(true)
          setErrorMessageVerifiedPhone(false)
          StorageHelpers.setItemLocalStorage('PHONE_VERIFIED', payload.phone)
        } else {
          await setModalData((prev) => ({...prev, verify_phone: response}))
          await setShowModal((prev) => ({...prev, verify_phone: true}))
        }
      })
      .catch((error: any) => {
        let errorMessage = error?.response?.data?.error?.message
        toast.error(errorMessage)
      })
  }, [newCancelToken, formik.values.phone])

  const verifyAddress = useCallback(
    async (values: any) => {
      const valuePayload = OrdersConfig.VALIDATE_ADDRESS.reduce((valuePayload, current) => {
        valuePayload[current] = values[`${current}`]
        return valuePayload
      }, {})

      try {
        const response = await OrderService.validateAddress(
          {address: valuePayload},
          {cancelToken: newCancelToken()}
        )
        const isValid =
          response.verifications.delivery.success && response.verifications.zip4.success
        if (isValid) {
          toast.success(intl.formatMessage({id: 'ADDRESS_IS_VALID'}))
        } else {
          toast.error(intl.formatMessage({id: 'ADDRESS_IS_INVALID'}))
        }
        setVerifiedAddress(isValid)
        return isValid
      } catch (error) {
        toast.error(intl.formatMessage({id: 'PLEASE_TRY_AGAIN_LATER'}))
        setVerifiedAddress(false)
        return false
      }
    },
    [intl, newCancelToken]
  )

  return (
    <>
      {showModal.verify_phone && (
        <VerificationPhoneModal
          show={showModal.verify_phone}
          data={modalData.verify_phone}
          handleClose={() => {
            setShowModal((prev) => ({...prev, verify_phone: false}))
          }}
          phone={formik.values.phone}
          fromPage='account_settings'
          verifiedPhone={verifiedPhone}
          updateVerifiedPhone={() => {
            setVerifiedPhone(true)
            setErrorMessageVerifiedPhone(false)
          }}
        />
      )}
      {validationErrors && (
        <ValidationErrorModal
          handleClose={() => {
            setValidationErrors(undefined)
          }}
          response={validationErrors}
        />
      )}

      <Modal
        id='gori_modal_accounting_setting'
        tabIndex={-1}
        centered
        dialogClassName='modal-size-xl h-auto'
        contentClassName='modal-padding'
        show={show}
        backdrop='static'
        onHide={handleCloseModal}
      >
        <Modal.Header closeButton>
          <Modal.Title bsPrefix={'fw-bolder fs-1'}>
            {intl.formatMessage({id: 'BUSINESS_PROFILE'})}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className='vh-75 overflow-auto'>
          <div className='row px-10'>
            <div className='col-md-6' id='onboard'>
              <div className='text-start'>
                <p className='fs-3 fw-bolder text-primary-deep'>
                  {intl.formatMessage({id: 'COMPANY_INFORMATION'})}
                </p>
                <div className='row'>
                  {PROFILE_EDIT_FIELD.ABOUT_YOUR.map((field, index) => {
                    if (field.type === 'text') {
                      return (
                        <div className='my-2 col-12' key={index}>
                          {field.name === 'phone' ? (
                            <div>
                              <InputInternationalPhoneFormik
                                labelClassName={'form-label fw-bolder text-dark fs-6 required'}
                                className='w-auto'
                                size='lg'
                                required={true}
                                label={intl.formatMessage({id: 'PHONE_NUMBER'})}
                                formik={formik}
                                name='phone'
                                onChange={(value) => checkChangePhone(value)}
                              />
                              {!verifiedPhone && (
                                <span
                                  className='fs-5 btn btn-advanced-editorial p-0 fw-bolder cursor-pointer ms-0'
                                  onClick={() => sendSmsOTP()}
                                >
                                  {intl.formatMessage({id: 'VERIFY_PHONE'})}
                                </span>
                              )}
                              {errorMessageVerifiedPhone && (
                                <div className='text-danger'>
                                  {intl.formatMessage({id: 'PHONE_MUST_BE_VERIFIED'})}
                                </div>
                              )}
                            </div>
                          ) : (
                            <InputTextFormik
                              className='col-12'
                              formik={formik}
                              name={field.name}
                              disabled={formik.isSubmitting || field?.disabled}
                              label={intl.formatMessage({id: field.label})}
                              labelClassName={clsx('fw-bolder', {required: field.required})}
                            />
                          )}
                        </div>
                      )
                    }
                    if (field.type === 'select') {
                      return (
                        <div className='my-2 col-12' key={index}>
                          <SelectFormik
                            checkFormik
                            emptyDefault={false}
                            options={field?.options || []}
                            formik={formik}
                            name={field.name}
                            placeholder={intl.formatMessage({id: field.placeholder})}
                            required={field.required}
                            isSearchable={false}
                            hasUseIntl={true}
                            label={intl.formatMessage({id: field.label})}
                            labelClassName={clsx('fw-bolder', {required: field.required})}
                          />
                        </div>
                      )
                    }
                    return null
                  })}
                </div>
              </div>
              <div className='text-start mt-6'>
                <p className='fs-3 fw-bolder mt-5'>
                  {intl.formatMessage({id: 'BUSINESS_ADDRESS'})}
                </p>
                <div className='row'>
                  {PROFILE_EDIT_FIELD.BUSINESS_ADDRESS.map((field, index) => {
                    if (field.type === 'text') {
                      return (
                        <div className={`my-2 ${field?.col}`} key={index}>
                          <InputTextFormik
                            className='col-12'
                            formik={formik}
                            name={field.name}
                            disabled={formik.isSubmitting}
                            label={intl.formatMessage({id: field.label})}
                            labelClassName={clsx('fw-bolder', {required: field.required})}
                          />
                        </div>
                      )
                    }
                    if (field.type === 'select') {
                      return (
                        <div className={clsx('my-2')} key={index}>
                          <SelectFormik
                            checkFormik
                            placeholder={''}
                            options={field?.options || []}
                            formik={formik}
                            name={field.name}
                            required={field.required}
                            label={intl.formatMessage({id: field.label})}
                            labelClassName={clsx('fw-bolder', {required: field.required})}
                          />
                        </div>
                      )
                    }
                    if (field.type === 'address') {
                      return (
                        <div className={`my-2 ${field?.col} address-complete`} key={index}>
                          <label
                            className={`form-label fw-bolder ${field.required ? 'required' : ''}`}
                          >
                            {intl.formatMessage({id: field.label})}
                          </label>
                          <AutocompleteAddress
                            formik={formik}
                            name={field.name}
                            label=''
                            onSelected={(selected) => {
                              selectedPlace(selected)
                            }}
                          />
                        </div>
                      )
                    }
                    return null
                  })}
                </div>
              </div>
            </div>
            <div className='col-md-6'>
              <div className='text-start'>
                <p className='fs-3 fw-bolder text-primary-deep'>
                  {intl.formatMessage({id: 'PRIMARY_CONTACT_EMAILS'})}
                </p>
                <div className='row'>
                  {PROFILE_EDIT_FIELD.PRIMARY_CONTACT.map((field, index) => {
                    let renderHeader
                    switch (index) {
                      case 0:
                        renderHeader = (
                          <p className='fs-3 fw-bolder'>
                            {intl.formatMessage({id: 'ALL_REPORTS'})}
                          </p>
                        )
                        break
                      case 2:
                        renderHeader = (
                          <p className='fs-3 fw-bolder mt-5'>
                            {intl.formatMessage({id: 'ACCOUNTING'})}
                          </p>
                        )
                        break
                      default:
                        renderHeader = null
                        break
                    }
                    if (field.type === 'text') {
                      return (
                        <div className={`my-2 ${field?.col}`} key={index}>
                          {renderHeader}
                          <InputTextFormik
                            className='col-12'
                            formik={formik}
                            name={field.name}
                            disabled={formik.isSubmitting}
                            label={intl.formatMessage({id: field.label})}
                            labelClassName={clsx('fw-bolder', {required: field.required})}
                          />
                        </div>
                      )
                    }
                    if (field.type === 'email') {
                      return (
                        <div className={`my-2 ${field?.col}`} key={index}>
                          {renderHeader}
                          <InputEmailFormik
                            label={intl.formatMessage({id: field.label})}
                            labelClassName={'fw-bolder'}
                            formik={formik}
                            name={field.name}
                            nameParent={field.nameParent}
                            maxEmailNumber={ConfigGeneral.MAX_EMAIL_NUMBER}
                            configAppend={{
                              name: intl.formatMessage({id: 'ADD'}),
                              position: 'right',
                              classInput: 'pe-25',
                              hasEvent: true,
                            }}
                          />
                        </div>
                      )
                    }
                    return renderHeader
                  })}
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className='d-flex justify-content-end'>
            <span
              className='d-flex justify-content-center align-items-center fs-5 btn btn-advanced-secondary cursor-pointer me-5 fw-bold'
              onClick={() => verifyAddress(formik.values)}
            >
              {intl.formatMessage({id: 'VERIFY_ADDRESS'})}
            </span>
            <Button
              className='btn btn-advanced-primary'
              label={intl.formatMessage({id: 'SAVE'})}
              loadingText={intl.formatMessage({id: 'SAVE'})}
              disabled={formik.isSubmitting}
              event={formik.submitForm}
              loading={formik.isSubmitting}
            />
          </div>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export {CompanyEditModal}
